import { Divider, Stack, Typography } from "@mui/material";
import React from "react";
import { booleanToDisplay } from "utils/displayUtils";
import { format } from "date-fns";
import { COUNTRIES, STATES, RELATIONSHIPS } from "@constants";
import { formatToPhoneNumber } from "utils/formatFormFields";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import useGetAllInsuranceCodes from "hooks/api/insurance/useGetAllInsuranceCodes";

const ASSESSMENT_BILLING_CAPTIONS: Record<string, string> = {
  INITIAL_ASSESSMENT:
    "This plan requires Assessment hours on the Initial Treatment Plan only",
  RE_ASSESSMENT_OR_90_DAYS:
    "This plan requires Assessment hours on 90-Day Progress Summaries and Re-Assessment Treatment Plans only",
  BOTH: "This plan requires Assessment hours on Initial Treatment Plans, 90-Day Progress Summaries, and Re-Assessment Treatment Plans",
  NEITHER: "This plan requires separate documentation for Assessment hours",
};

const ListElement = ({ title, value }: { title: string; value?: string | number | null }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography variant="body2">{title}</Typography>
    <Typography variant="body2" fontWeight={500} textAlign="right">
      {value ? value : "-"}
    </Typography>
  </Stack>
);

const SectionContainer = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack gap={6}>
    <Typography variant="subtitle2" color="text.secondary">
      {title}
    </Typography>
    <Stack gap="16px">{children}</Stack>
  </Stack>
);

interface InsurancePanelViewProps {
  insurance: any;
}

export default function InsurancePanelViewComponent({
  insurance,
}: InsurancePanelViewProps) {
  const { subscriber_dob, subscriber_gender, subscriber_phone, effective_date, expiration_date } =
    insurance ?? {};

  const { insuranceCodes } = useGetAllInsuranceCodes();

  const resolveCodeLabel = (id: number | null | undefined): string => {
    if (!id) return "-";
    const found = insuranceCodes?.find((c) => c.id === id);
    return found?.insurance_code ?? "-";
  };

  return (
    <Stack gap={7}>
      <SectionContainer title="Coverage">
        <ListElement title="Primary insurance" value={booleanToDisplay(insurance?.is_primary)} />
        <ListElement title="Insurance company" value={insurance?.insurance_name} />
        <ListElement title="Insurance plan" value={insurance?.insurance_plan_name} />
        <ListElement title="Payor ID" value={insurance?.payer_id} />
        <ListElement title="Member ID" value={insurance?.member_id} />
        <ListElement title="Group number" value={insurance?.group_number} />
        <ListElement
          title="Effective date"
          value={effective_date && format(new Date(effective_date), "P")}
        />
        <ListElement
          title="Expiration date"
          value={expiration_date && format(new Date(expiration_date), "P")}
        />
        <ListElement title="Medicaid" value={booleanToDisplay(insurance?.is_medicaid)} />
      </SectionContainer>
      <Divider />
      <Stack gap={6}>
        <Typography variant="subtitle2" color="text.secondary">
          Payor CPT Mapping
        </Typography>
        <Stack gap="16px">
          <Stack>
            <ListElement
              title="Assessment"
              value={resolveCodeLabel(insurance?.assessment_cpt_code_id)}
            />
            {insurance?.approve_assessment_billing_at &&
              ASSESSMENT_BILLING_CAPTIONS[insurance.approve_assessment_billing_at] && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontStyle="italic"
                  lineHeight={1.4}
                >
                  {ASSESSMENT_BILLING_CAPTIONS[insurance.approve_assessment_billing_at]}
                </Typography>
              )}
          </Stack>
          <ListElement
            title="Direct Therapy"
            value={resolveCodeLabel(insurance?.direct_therapy_cpt_code_id)}
          />
          <ListElement
            title="Caregiver Training"
            value={resolveCodeLabel(insurance?.caregiver_training_cpt_code_id)}
          />
          <ListElement
            title="Caregiver Mediated"
            value={resolveCodeLabel(insurance?.caregiver_mediated_cpt_code_id)}
          />
          <ListElement
            title="Direct Protocol Modification"
            value={resolveCodeLabel(insurance?.protocol_modification_direct_cpt_code_id)}
          />
          <ListElement
            title="Indirect Protocol Modification"
            value={resolveCodeLabel(insurance?.protocol_modification_indirect_cpt_code_id)}
          />
        </Stack>
      </Stack>
      <Divider />
      <SectionContainer title="Insurance subscriber">
        <ListElement title="First name" value={insurance?.subscriber_first_name} />
        <ListElement title="Last name" value={insurance?.subscriber_last_name} />
        <ListElement
          title="Date of birth"
          value={subscriber_dob && format(dateWithoutTimezone(subscriber_dob), "P")}
        />
        <ListElement title="Gender" value={subscriber_gender} />
        <ListElement title="Email address" value={insurance?.subscriber_email} />
        <ListElement title="Phone number" value={formatToPhoneNumber(subscriber_phone)} />
        <ListElement
          title="Relationship to client"
          value={insurance?.subscriber_relationship ? RELATIONSHIPS[insurance.subscriber_relationship as keyof typeof RELATIONSHIPS] : undefined}
        />
        <ListElement title="Country" value={insurance?.subscriber_country ? COUNTRIES[insurance.subscriber_country as keyof typeof COUNTRIES] : undefined} />
        <ListElement title="State" value={insurance?.subscriber_state ? STATES[insurance.subscriber_state as keyof typeof STATES] : undefined} />
        <ListElement title="City" value={insurance?.subscriber_city} />
        <ListElement title="ZIP code" value={insurance?.subscriber_zip} />
        <ListElement title="Address" value={insurance?.subscriber_address} />
        <ListElement title="Address 2" value={insurance?.subscriber_address2} />
      </SectionContainer>
    </Stack>
  );
}
